/**
 * Keeping the iOS app useful without signal.
 *
 * Trips: after each successful load, the trips page saves the customer's own
 * bookings on the phone, so their trips still open on a plane or in a
 * terminal with no service. App only — a browser on a shared computer
 * shouldn't keep anyone's itinerary — and never an admin's all-bookings list.
 * The copy is exactly what the server sent: screens opened from it can save
 * changes back, so it must never hold edited or masked values. Signing out
 * erases it with the rest of localStorage (Layout's clearSessionAndRedirect).
 *
 * Sign-in: offline, Supabase can't refresh an expired sign-in (it retries for
 * ~30s, then getSession() returns no session). That's the network, not a
 * sign-out. Supabase keeps the saved sign-in, refreshes it once back online,
 * and signs out properly (SIGNED_OUT) only if the server rejects it.
 */
import { useEffect, useState } from 'react';
import { isNativeApp } from '@/native/bootstrap';

const TRIPS_KEY = 'defera.offlineTrips.v1';
const USER_KEY = 'defera.user';
// A customer's own trips are a few KB each; a copy this big is not one
// customer's trips and doesn't belong on a phone.
const MAX_SAVED_CHARS = 1_000_000;

function browserStorage() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null; // storage blocked
  }
}

export function isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** A getSession() failure caused by the network, not by the sign-in ending.
 *  (Supabase's AuthRetryableFetchError: no connection, or a 5xx from auth.) */
export function isNetworkAuthError(error) {
  if (!error) return false;
  return error.name === 'AuthRetryableFetchError' || isOffline();
}

/** The signed-in customer as Layout last saved them (the 'defera.user' cache). */
export function readCachedUser(storage = browserStorage()) {
  try {
    const user = JSON.parse(storage?.getItem(USER_KEY) || 'null');
    return user && user.id && user.email && user.version >= 4 ? user : null;
  } catch {
    return null;
  }
}

/** Whether Supabase's saved sign-in on this device belongs to this user —
 *  expired or not. Offline, that's as signed in as anyone can know. */
export function hasSavedSignIn(userId, storage = browserStorage()) {
  if (!userId || !storage) return false;
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (!/^sb-.+-auth-token$/.test(key || '')) continue;
    try {
      const saved = JSON.parse(storage.getItem(key));
      if (saved?.refresh_token && saved?.user?.id === userId) return true;
    } catch {
      /* not a session */
    }
  }
  return false;
}

/** The account to show while Supabase can't be reached, or null. */
export function offlineAccount(storage = browserStorage()) {
  const user = readCachedUser(storage);
  return user && hasSavedSignIn(user.id, storage) ? user : null;
}

function isAdmin(user) {
  return user?.role === 'admin' || Boolean(user?.app_metadata?.roles?.includes('admin'));
}

export function saveOfflineTrips(user, bookings, { now = Date.now(), storage = browserStorage(), enabled = isNativeApp } = {}) {
  if (!enabled || !storage || !user?.id || !Array.isArray(bookings) || isAdmin(user)) return false;
  try {
    const json = JSON.stringify({ userId: user.id, savedAt: now, bookings });
    if (json.length > MAX_SAVED_CHARS) {
      storage.removeItem(TRIPS_KEY);
      return false;
    }
    storage.setItem(TRIPS_KEY, json);
    return true;
  } catch {
    return false; // storage full: the app just stays online-only
  }
}

/** { savedAt, bookings } saved for this user, or null. */
export function loadOfflineTrips(userId, { storage = browserStorage(), enabled = isNativeApp } = {}) {
  if (!enabled || !storage || !userId) return null;
  try {
    const saved = JSON.parse(storage.getItem(TRIPS_KEY) || 'null');
    if (!saved || saved.userId !== userId || !Array.isArray(saved.bookings)) return null;
    return { savedAt: saved.savedAt, bookings: saved.bookings };
  } catch {
    return null;
  }
}

/** Live connection state, for screens that change what they show offline. */
export function useOnline() {
  const [online, setOnline] = useState(() => !isOffline());
  useEffect(() => {
    const update = () => setOnline(!isOffline());
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}
